<template>
  <v-menu>
    <template #activator="{ props: menuProps }">
      <v-tooltip location="top" transition="fade-transition">
        <template #activator="{ props: tooltipProps }">
          <app-button v-if="multiFiles" type="icon" v-bind="{ ...menuProps, ...tooltipProps }" class="btn-ellipsis">
            <app-icon-ds icon="ellipsis" size="xs" />
            <span>{{t('moreActions')}}</span>
          </app-button>
          <app-button v-else type="icon" v-bind="{ ...menuProps, ...tooltipProps }" class="btn-ellipsis" size="small" data-locator="other-actions-menu-button">
            <app-icon-ds icon="ellipsis" size="xs" />
          </app-button>
        </template>
        <span>{{ $t('common.additionalActions') }}</span>
      </v-tooltip>
    </template>

    <v-list data-locator="checklist-attachment-menu">
      <OfficeOnlineEditionButton v-if="onlyOneFile && isOfficeEditionButtonShowed(file)" :document="file" :btnHasIconAndText="true"/>
      
      <v-list-item density="compact" link @click="OPEN_DOCUMENTS_DOWNLOAD_FILES(handledFiles)">
        <v-list-item-title>
          <div class="d-flex align-center">
            <app-icon-ds
              icon="arrow-down-to-line"
              size="xs"
              class="mr-3 text-grey-600"
              data-locator="download-menu-item"
            />
            <span>{{$t('common.download')}}</span>
          </div>
        </v-list-item-title>
      </v-list-item>

      <template v-if="isCurrentUserPm">
        <v-list-item density="compact" link data-locator="share-menu-item" @click="handleMenuAction('share')">
          <v-list-item-title>
            <app-icon-ds icon="share" size="xs" class="mr-2 text-grey-600" />
            {{ $t('common.share') }}
          </v-list-item-title>
        </v-list-item>
      </template>

      <v-list-item
        v-if="versionManagementType && onlyOneFile"
        link
        density="compact"
        :data-locator="versionDataLocator(versionManagementType)"
        @click="handleMenuAction(versionManagementType)"
      >
        <v-list-item-title>
          <app-icon-ds icon="clock-rotate-left" size="xs" class="mr-2 text-grey-600" />
          {{ t(versionManagementType) }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="isCurrentUserPm && file.numbering && onlyOneFile"
        link
        @click="handleMenuAction('numbering')"
      >
        <v-list-item-title>
          <app-icon-ds icon="hashtag" size="xs" class="mr-2 text-grey-600" />
          {{ t('numbering') }}
        </v-list-item-title>
      </v-list-item>

      <template v-if="file.canWrite">
        <v-divider class="mx-2 my-1" />
        <v-list-item density="compact" link data-locator="move-menu-item" @click="handleMenuAction('move')">
          <v-list-item-title>
            <app-icon-ds icon="arrow-turn-right" size="xs" class="mr-2 text-grey-600" />
            {{ t('move') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item density="compact" link data-locator="duplicate-menu-item" @click="handleMenuAction('duplicate')">
          <v-list-item-title>
            <app-icon-ds icon="copy" size="xs" class="mr-2 text-grey-600" />
            {{ t('duplicate') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item density="compact" link v-if="onlyOneFile" data-locator="rename-menu-item" @click="handleMenuAction('rename')" class="rename-button">
          <v-list-item-title>
            <app-icon-ds icon="pen" size="xs" class="mr-2 text-grey-600" />
            {{ t('rename') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item density="compact" link data-locator="delete-menu-item" @click="handleMenuAction('delete')" class="text-error">
          <v-list-item-title>
            <app-icon-ds icon="trash-can" size="xs" class="mr-2 text-error" />
            {{ $t('common.delete') }}
          </v-list-item-title>
        </v-list-item>
        <v-divider class="mx-2 my-1" />
      </template>

      <v-list-item
        v-if="enableAddToSigningChecklist"
        link
        density="compact"
        data-locator="add-to-signing-checklist-menu-item"
        @click="handleMenuAction('signingChecklistAdd')"
      >
        <v-list-item-title>
          <app-icon-ds icon="file-signature" size="xs" class="mr-2 text-grey-600" />
          {{ t('signingChecklistAdd') }}
        </v-list-item-title>
      </v-list-item>

      <ArchiveAddAccessListItem
        :routeOptions="archiveAddRouteOptions"
        :btnHasIconAndText="true"
        compact
        data-locator="archive-menu-item"
      />

      <PolyOfficeListItemExport
        v-if="polyOfficeEnabled"
        :fileSize="file.size"
        :btnHasIconAndText="true"
        @polyOfficeOpenDialog="polyOfficeOpenDialogHandler(file)"
        compact
      />

      <v-list-item
        v-if="imanageEnabledAndLogged"
        link
        density="compact"
        data-locator="imanage-export-menu-item"
        @click="isExportToImanageDialogOpen = true"
      >
        <v-list-item-title class="d-flex align-center">
          <img
            src="@/assets/img/icons/iManage.svg"
            alt="iManage icon"
            class="mr-2"
          />
          {{ t('exportToImanage') }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <DocumentsNumberingDialog
    v-if="numberingDialogIsOpen"
    :document="file"
    @close="numberingDialogIsOpen = false"
    @onSuccess="updateDocumentsNumbering"
  />

  <IManageFolderPickerDialog
    v-if="isExportToImanageDialogOpen"
    v-model:is-open="isExportToImanageDialogOpen"
    @folders="exportFilesToImanage"
  />
</template>

<script>

import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'

import ArchiveAddAccessListItem from '@/common/archives/ArchiveAddAccessListItem.vue'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import OfficeOnlineEditionButton from '@/common/office/OfficeOnlineEditionButton.vue'
import { DocumentsExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeListItemExport from '@/common/polyOffice/PolyOfficeListItemExport.vue'
import { isFileSignable } from '@/common/utils/files'
import { canFilesBeAddedToChecklist } from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import DocumentsNumberingDialog from '@/project/documents/dialogs/DocumentsNumberingDialog.vue'
import { SIGNING_CHECKLIST_ADD_ROUTE_NAME } from '@/router'
import { SET_CHECKLIST_VERSIONING_PANE } from '@/store/modules/checklist/action_types'
import { SET_TABLE_TASK_PANE, SET_RENAME_ATTACHED_FILE_DIALOG } from '@/store/modules/checklist/mutation_types'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { OPEN_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'
import { POST_FILDERS_TO_IMANAGE } from '@/store/modules/imanage/action_types'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'

export default {
  name: 'ChecklistTableTaskAttachedFilesItemMenu',
  components: {
    IManageFolderPickerDialog,
    ArchiveAddAccessListItem,
    DocumentsNumberingDialog,
    OfficeOnlineEditionButton,
    PolyOfficeListItemExport,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    file: {
      type: Object,
      required: true,
    },
    multiFiles: {
      type: Array,
      required: false,
    },
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableTaskAttachedFilesItemMenu' })
  },
  data () {
    return {
      isExportToImanageDialogOpen: false,
      numberingDialogIsOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['currentUserRights', 'isCurrentUserPm', 'maxSignableSize', 'isWopiEnabled']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    handledFiles () {
      if (this.multiFiles && Array.isArray(this.multiFiles) && this.multiFiles.length) {
        return this.multiFiles
      }
      return this.file ? [this.file] : []
    },
    handledFilesIds () {
      return this.handledFiles.map(file => file.id).join(',')
    },
    onlyOneFile () {
      return this.handledFiles.length === 1
    },
    archiveAddRouteOptions () {
      return {
        state: {
          files: this.handledFiles,
        },
        query: {
          fileIds: this.handledFilesIds,
          fromMnemo: this.mnemo,
        },
      }
    },
    isFileSignableComputed () {
      return isFileSignable(this.file, this.maxSignableSize)
    },
    canFilesBeAddedToChecklist () {
      return canFilesBeAddedToChecklist(this.handledFiles, this.isCurrentUserPm, this.maxSignableSize)
    },
    enableAddToSigningChecklist () {
      if (this.onlyOneFile) {
        return this.isCurrentUserPm && this.isFileSignableComputed
      } else {
        return this.canFilesBeAddedToChecklist
      }
    },
    versionManagementType () {
      if (this.isCurrentUserPm || this.currentUserRights?.canUploadVersions) {
        return 'manageVersions'
      } else if (this.file.nbVersions > 1) {
        return 'showVersions'
      } else {
        return null
      }
    },
  },
  methods: {
    ...mapActions('checklist', [SET_CHECKLIST_VERSIONING_PANE]),
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.OPEN_DOCUMENTS_DELETION_DIALOG]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    ...mapActions('imanage', [POST_FILDERS_TO_IMANAGE]),
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    ...mapActions('documentsDownloadFiles', [OPEN_DOCUMENTS_DOWNLOAD_FILES]),

    handleMenuAction (action) {
      switch (action) {
        case 'share':
          this.prepareSharePane()
          break
        case 'move':
          this.prepareMovePane()
          break
        case 'duplicate':
          this.prepareDuplicatePane()
          break
        case 'manageVersions':
          this.openVersioningPane('manage')
          break
        case 'showVersions':
          this.openVersioningPane('history')
          break
        case 'signingChecklistAdd':
          this.$router.push({
            name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
            state: {
              files: this.handledFiles,
            },
            query: {
              fileIds: this.handledFilesIds,
            },
          })
          break
        case 'delete':
          this.openDeleteAttachedFileDialog()
          break
        case 'rename':
          this.openRenameAttachedFileDialog()
          break
        case 'numbering':
          this.numberingDialogIsOpen = true
          break
      }
    },
    versionDataLocator (action) {
      if (action === 'manageVersions') {
        return 'manage-versions-menu-item'
      } else if (action === 'showVersions') {
        return 'show-versions-menu-item'
      }
    },
    prepareSharePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.$store.commit(`checklist/${SET_TABLE_TASK_PANE}`, {
        isOpen: true,
        tab: 'share',
        documents: this.handledFiles,
        parent: this.file.parent,
      })
    },
    prepareMovePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.$store.commit(`checklist/${SET_TABLE_TASK_PANE}`, {
        isOpen: true,
        tab: 'move',
        documents: this.handledFiles,
        parent: this.file.parent,
      })
    },
    prepareDuplicatePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.$store.commit(`checklist/${SET_TABLE_TASK_PANE}`, {
        isOpen: true,
        tab: 'duplicate',
        documents: this.handledFiles,
        parent: this.file.parent,
      })
    },
    openDeleteAttachedFileDialog () {
      this.OPEN_DOCUMENTS_DELETION_DIALOG(this.handledFiles)
    },
    openRenameAttachedFileDialog () {
      this.$store.commit(`checklist/${SET_RENAME_ATTACHED_FILE_DIALOG}`, {
        isOpen: true,
        fileToRename: this.file,
      })
    },
    openVersioningPane (tab) {
      this.SET_CHECKLIST_VERSIONING_PANE({ isOpen: true, documents: [this.file], tab })
    },
    updateDocumentsNumbering (file) {
      this.file.numbering = file.numbering
    },
    async polyOfficeOpenDialogHandler (file) {
      const selectedFileIds = this.onlyOneFile? [file.id] : this.handledFiles.map(attachedFile => attachedFile.id)
      const payload = new DocumentsExport(this.mnemo, selectedFileIds)
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.t('polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
    isOfficeEditionButtonShowed (document) {
      return this.isWopiEnabled && document.canEdit && this.isCurrentUserPm
    },
    async exportFilesToImanage (target) {
      try {
        await this.POST_FILDERS_TO_IMANAGE({
          mnemo: this.mnemo,
          data: {
            files: this.multiFiles? this.multiFiles.map(file => file.id) : [this.file.id],
            targetId: target.selected,
          },
        })
        toast.success(this.t('imanage.exportSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.t('imanage.exportFail'))
      }
    },
  },
}
</script>
<style scoped lang="scss">
:deep(.app-button.app-button-icon.btn-ellipsis) {
  width: 28px !important;
  height: 28px !important;
}
:deep(.v-list-item-title) {
  letter-spacing: 0;
}
</style>
